/**
 * The controller page's Events tab: commands sent and what the site answered, and its events.
 * Split out of pages/irrigation/EspNowControllerDetail.tsx.
 */
import { useOrgTimezone, formatDateTimeInTz } from '@/utils/timezone';
import {
  type EspNowRouter,
  type EspNowEvent,
  type FieldCommand,
  type ConfigAckPayload,
  CONFIG_ACK_STATUS,
} from '../../../services/espnowIrrigation';
import { nodeNoun } from '../../../utils/nodeLabel';

// ─── Commands Table ───────────────────────────────────────────────────────────

/**
 * What we asked this site to do, and what it said back.
 *
 * Every ack the hardware sends has always been stored — status, detail,
 * status_code, armed — and until now none of it was readable anywhere. The only
 * consumer was the schedule push's ack poll, which aggregates, keeps just the
 * failures, and runs for sixty seconds after a push and never again. So a
 * command that failed at any other moment left a precise explanation in the
 * database that no screen would ever show.
 *
 * The case that forced this: a forced run on a bridge predating RUN_PROGRAM was
 * acked `unsupported command type 'RUN_PROGRAM'` — exactly the sentence needed
 * to diagnose it — while the operator watched a site claim to be watering and
 * do nothing.
 */
export function CommandsTable({ commands, routers }: { commands: FieldCommand[]; routers: EspNowRouter[] }) {
  const orgTz = useOrgTimezone();

  const statusStyle: Record<string, { chip: string; label: string }> = {
    QUEUED:     { chip: 'bg-gray-100 text-gray-600',     label: 'Queued' },
    DISPATCHED: { chip: 'bg-blue-100 text-blue-700',     label: 'Collected' },
    PENDING:    { chip: 'bg-amber-100 text-amber-700',   label: 'Pending' },
    OK:         { chip: 'bg-green-100 text-green-700',   label: 'OK' },
    CONFIRMED:  { chip: 'bg-green-100 text-green-700',   label: 'Confirmed' },
    ERROR:      { chip: 'bg-red-100 text-red-700',       label: 'Failed' },
  };

  function routerLabel(mac: unknown): string | null {
    if (typeof mac !== 'string' || !mac) return null;
    const r = routers.find(x => x.router_mac.toUpperCase() === mac.toUpperCase());
    return r?.name || mac.slice(-6);
  }

  /** The one line of context that says which command this was. */
  function target(cmd: FieldCommand): string {
    const p = cmd.payload || {};
    const node = routerLabel(p.router_mac);
    const relay = p.relay ?? p.relay_index;
    if (node && relay != null) return `${node} · relay ${relay}`;
    if (node) return node;
    if (typeof p.version === 'string') return `v${p.version}`;
    // A program id is not something anyone can read. Saying so is more use than
    // showing 36 characters of UUID.
    if (p.program_id) return 'schedule program';
    return '—';
  }

  if (commands.length === 0) {
    return (
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Commands sent</h3>
        <div className="flex items-center justify-center h-24 text-gray-400 text-sm">
          Nothing has been sent to this controller yet.
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-1">Commands sent</h3>
      <p className="text-xs text-gray-500 mb-3">
        What the site was asked to do, and what it answered. A failure here is
        usually the whole explanation for something that did not happen.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
              <th className="py-2 pr-4 font-medium">Sent</th>
              <th className="py-2 pr-4 font-medium">Command</th>
              <th className="py-2 pr-4 font-medium">Target</th>
              <th className="py-2 pr-4 font-medium">Result</th>
              <th className="py-2 font-medium">What it said</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {commands.map(cmd => {
              const style = statusStyle[cmd.status] || { chip: 'bg-gray-100 text-gray-600', label: cmd.status };
              const failed = cmd.status === 'ERROR';
              // Stored but inert. A success by the ack's reckoning and a dry
              // block by any other, so it must not read as a plain OK.
              const inert = cmd.armed === false;
              return (
                <tr key={cmd.cmd_id} className={failed ? 'bg-red-50/40' : undefined}>
                  <td className="py-2 pr-4 whitespace-nowrap text-gray-500">
                    {formatDateTimeInTz(cmd.created_at, orgTz)}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap font-mono text-xs text-gray-700">
                    {cmd.type}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap text-gray-600">{target(cmd)}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${style.chip}`}>
                      {style.label}
                    </span>
                    {inert && (
                      <span className="ml-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                        not armed
                      </span>
                    )}
                  </td>
                  <td className={`py-2 ${failed ? 'text-red-600 font-medium' : inert ? 'text-amber-700' : 'text-gray-500'}`}>
                    {cmd.detail || (cmd.status === 'QUEUED' ? 'Not collected yet' : '—')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Events Table ─────────────────────────────────────────────────────────────

export function EventsTable({ events, routers, transport }: { events: EspNowEvent[]; routers: EspNowRouter[]; transport?: string | null }) {
  const orgTz = useOrgTimezone();
  const noun = nodeNoun(transport);
  const typeColour: Record<string, string> = {
    ANNOUNCE:       'bg-green-100 text-green-700',
    HEARTBEAT_ACK:  'bg-blue-100 text-blue-700',
    OFFLINE:        'bg-red-100 text-red-700',
    CONFIG_ACK:     'bg-purple-100 text-purple-700',
    OTA_ACK:        'bg-indigo-100 text-indigo-700',
    SCHEDULE_STATE: 'bg-teal-100 text-teal-700',
    // The XBee bridge's own events. A sequence that did not run is the most
    // consequential line this table can show - water that did not happen, on a
    // farm nobody is standing in - and it was rendering in the same grey as a
    // heartbeat.
    SEQUENCE_SKIPPED:  'bg-red-100 text-red-700',
    VALVE_FAILURE:     'bg-red-100 text-red-700',
    NO_XBEE_RESPONSE:  'bg-red-100 text-red-700',
    PREFLIGHT_WARNING: 'bg-amber-100 text-amber-700',
    CLOCK_UNTRUSTED:   'bg-amber-100 text-amber-700',
    PROGRAM_STARTED:   'bg-blue-100 text-blue-700',
    SEQUENCE_STARTED:  'bg-blue-100 text-blue-700',
    PROGRAM_COMPLETED: 'bg-green-100 text-green-700',
    COORDINATOR_OK:    'bg-green-100 text-green-700',
    NODE_CANDIDATE:    'bg-amber-100 text-amber-700',
  };

  function routerLabel(mac: string | null): string {
    if (!mac) return '—';
    const r = routers.find(x => x.router_mac === mac);
    return r?.name || mac;
  }

  /**
   * A CONFIG_ACK carrying `status: "OK"` is not a promise that the program will
   * water — codes 3 and 4 store the schedule but leave it unarmed. Read the ack
   * into plain language so an inert program cannot pass as a success.
   */
  function ackSummary(ev: EspNowEvent): { text: string; tone: 'ok' | 'warn' | 'error' } | null {
    if (ev.event_type === 'OTA_ACK') {
      const status = String(ev.payload.status ?? '');
      if (status === 'STARTING') return { text: 'Downloading firmware', tone: 'ok' };
      if (status === 'FAILED') return { text: 'Mesh delivery failed', tone: 'error' };
      return status ? { text: status, tone: 'warn' } : null;
    }
    // Anything from the bridge explains itself in `detail`. Showing it is the
    // difference between "SEQUENCE_SKIPPED" and "Sequence 2 of Block A skipped:
    // 1 of 3 nodes did not answer (0013A2...)".
    const detail = ev.payload?.detail;
    if (typeof detail === 'string' && detail) {
      const tone: 'ok' | 'warn' | 'error' =
        ev.event_type === 'SEQUENCE_SKIPPED' || ev.event_type === 'VALVE_FAILURE'
        || ev.event_type === 'NO_XBEE_RESPONSE'
          ? 'error'
          : ev.event_type === 'PREFLIGHT_WARNING' || ev.event_type === 'CLOCK_UNTRUSTED'
            ? 'warn'
            : 'ok';
      return { tone, text: detail };
    }
    if (ev.event_type !== 'CONFIG_ACK') return null;
    const p = ev.payload as ConfigAckPayload;
    const known = p.status_code != null ? CONFIG_ACK_STATUS[p.status_code] : undefined;
    const relay = p.relay_id != null ? `Relay ${p.relay_id}: ` : '';
    if (known) return { text: relay + known.label, tone: known.tone };
    // Older firmware without status_code — fall back to armed/status/detail.
    if (p.armed === false) {
      return { text: relay + (p.detail || 'stored, but not armed — will not run'), tone: 'warn' };
    }
    if (p.status === 'ERROR') return { text: relay + (p.detail || 'rejected'), tone: 'error' };
    if (p.status === 'OK') return { text: relay + 'armed', tone: 'ok' };
    return null;
  }

  const ackTone: Record<'ok' | 'warn' | 'error', string> = {
    ok:    'text-green-600',
    warn:  'text-amber-600 font-medium',
    error: 'text-red-600 font-medium',
  };

  if (events.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
        No events recorded yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wide">
            <th className="text-left py-2 pr-4">Time</th>
            <th className="text-left py-2 pr-4">Type</th>
            <th className="text-left py-2 pr-4 hidden sm:table-cell">{noun.one}</th>
            <th className="text-left py-2 pr-4">Result</th>
            <th className="text-left py-2 hidden lg:table-cell">Payload</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {events.map(ev => {
            const ack = ackSummary(ev);
            return (
              <tr key={ev.event_id} className="hover:bg-gray-50">
                <td className="py-2 pr-4 text-xs text-gray-500 whitespace-nowrap">
                  {formatDateTimeInTz(ev.timestamp, orgTz)}
                </td>
                <td className="py-2 pr-4">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColour[ev.event_type] ?? 'bg-gray-100 text-gray-600'}`}>
                    {ev.event_type}
                  </span>
                </td>
                <td className="py-2 pr-4 text-xs text-gray-500 hidden sm:table-cell" title={ev.router_mac ?? undefined}>
                  {routerLabel(ev.router_mac)}
                </td>
                <td className="py-2 pr-4 text-xs">
                  {ack
                    ? <span className={ackTone[ack.tone]}>{ack.text}</span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="py-2 text-xs text-gray-500 max-w-xs truncate font-mono hidden lg:table-cell">
                  {JSON.stringify(ev.payload)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

